<template>
  <div class="price-request-details">
    <FeedbackNotification
      v-if="shouldShowFeedbackNotification"
      :is-accepted="isCompletedOrAccepted"
      :is-estimate-request="isEstimateRequest"
      @read-feedback="openFeedbackPanel"
    />

    <div class="price-request-details__grid">
      <DetailsItem
        v-for="item in detailItems"
        :key="item.label"
        :label="item.label"
        :value="item.value"
        :is-loading="item.loading"
        :class="{
          'details-item--has-survey': item.isSurvey && isAttendSurveyVisible,
        }"
      >
        <template v-if="item.isSurvey && isAttendSurveyVisible" #label-right>
          <div
            class="survey-attendance-control"
            :class="{ 'is-disabled': isJoiningSurvey }"
          >
            <div class="survey-attendance-info">
              <span class="survey-attendance-label">
                {{ hasJoinedSurvey ? "Deltar" : "Deltar ikke" }}
              </span>
              <span class="survey-attendance-description">
                Registrer deltakelse på befaring
              </span>
            </div>
            <ToggleSwitch
              v-model="hasJoinedSurvey"
              :disabled="isJoiningSurvey"
              :input-id="`survey-toggle-${item.label}`"
              aria-describedby="survey-toggle-help"
              class="survey-toggle-switch"
              @change="toggleSurveyAttendance"
            />
            <span id="survey-toggle-help" class="sr-only">
              Slå på for å delta i felles befaring, eller slå av for ikke å
              delta
            </span>
          </div>
        </template>
        <template v-if="item.isTags" #value>
          <div class="customer-tags">
            <Tag
              v-for="tag in item.tags"
              :key="tag"
              :value="tag"
              severity="secondary"
              rounded
              class="customer-tag"
            />
            <span v-if="!item.tags || item.tags.length === 0" class="no-tags">
              Ingen tags
            </span>
          </div>
        </template>
      </DetailsItem>
    </div>

    <CollapsibleSection
      :is-truncated="isWorkDescriptionTruncated"
      :content="effectiveSharedMessage"
      label="Arbeidsbeskrivelse"
      @toggle="toggleWorkDescription"
    />

    <div class="price-request-details__full-width">
      <div class="price-request-details__label">Vedlegg</div>
      <div class="price-request-details__divider" />
      <div
        v-if="mappedPriceRequestAttachments.length"
        class="price-request-details__attachments"
      >
        <Attachment
          v-for="(attachment, index) in mappedPriceRequestAttachments"
          :key="index"
          :download-handler="attachment.downloadHandler"
          :file-name="attachment.fileName"
          class="price-request-details__attachment"
        />
      </div>
      <div v-else class="price-request-details__no-attachment">
        <p class="price-request-details__no-attachment-text">Ingen vedlegg</p>
      </div>
    </div>

    <div class="price-request-details__full-width">
      <div class="price-request-details__label">Fellesmeldinger</div>
      <div class="price-request-details__divider" />
      <PublicNotes
        :tender="tender"
        :price-request="priceRequest"
        :supplier-id="supplierId"
      />
    </div>

    <div class="price-request-details__full-width">
      <div class="price-request-details__label">Vilkår</div>
      <div class="price-request-details__divider" />
      <template v-if="!isLoadingFrameAgreement">
        <Attachment
          v-for="attachment in displayedAttachments"
          :key="attachment.fileName"
          :file-name="attachment.fileName"
          :download-handler="attachment.downloadHandler"
          class="price-request-details__attachment"
        />
        <p
          v-if="displayedAttachments?.length === 0"
          class="price-request-details__no-attachment-text"
        >
          Ingen vedlegg
        </p>
      </template>
      <div v-else class="price-request-details__skeleton">
        <Skeleton
          v-for="n in 2"
          :key="n"
          width="100%"
          height="40px"
          class="mb-2"
          border-radius="8px"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, toRefs, watch } from "vue";
import Skeleton from "primevue/skeleton";
import ToggleSwitch from "primevue/toggleswitch";
import Tag from "primevue/tag";
import { usePriceRequestFrameAgreement } from "@/composables/usePriceRequestFrameAgreement";
import { usePriceRequestFeedback } from "@/composables/usePriceRequestFeedback";
import { usePriceRequestDetails } from "@/composables/usePriceRequestDetails";
import DetailsItem from "@/components/PriceRequestDetails/DetailsItem.vue";
import CollapsibleSection from "@/components/PriceRequestDetails/CollapsibleSection.vue";
import Attachment from "@/components/Attachment/Attachment.vue";
import FeedbackNotification from "@/components/FeedbackNotification/FeedbackNotification.vue";
import PublicNotes from "@/components/PublicNotes/PublicNotes.vue";
import { Tender, PriceRequestDetails } from "@/stores/tender/types";
import { useFileAttachmentApi } from "@/services/api/useFileAttachmentApi";
import useFileDownload from "@/composables/useFileDownload";
import { useRouter } from "vue-router";
import { RequestStateType } from "@/custom-types/GeneralTypes";
import { useTenderState } from "@/composables/useTenderState";
import { useTenderApi } from "@/services/api/useTenderApi";

interface Props {
  tender: Tender;
  supplierId: number;
  supplierRequestId: number;
  priceRequestDetails?: PriceRequestDetails | null;
  serviceCategory: string;
  serviceOfferId: number;
  deadlineDate: string | object;
  isEstimateRequest?: boolean;
}

interface FrameAgreementAttachment {
  fileName: string;
  attachmentId: string;
}

interface DisplayAttachment {
  fileName: string;
  downloadHandler: () => Promise<void>;
}

// Add these after the existing imports
enum AgreementAndTermsTitles {
  Agreement = "Avtale",
  Terms = "Avtalevilkår",
}

const agreementAndTermsCms = [
  {
    title: AgreementAndTermsTitles.Terms,
    link: "https://cdn.sanity.io/files/0ls0csm4/production/4fbf969e8e608afe6d1452e7d3640b40d32a6cc1.pdf",
  },
  {
    title: AgreementAndTermsTitles.Agreement,
    link: "https://cdn.sanity.io/files/0ls0csm4/production/4fbf969e8e608afe6d1452e7d3640b40d32a6cc1.pdf",
  },
];

const props = withDefaults(defineProps<Props>(), {
  priceRequestDetails: null,
});

const { isCompletedOrAccepted } = useTenderState(props);

const hasFrameAgreement = computed(
  () => props.priceRequestDetails?.hasFrameAgreement ?? false,
);

const customerOrganizationId = computed(
  () => props.priceRequestDetails?.customerOrgId ?? null,
);

const {
  effectiveReferenceCode,
  effectiveServiceCategory,
  effectiveRequestSent,
  effectiveDesiredStartDate,
  effectiveJointInspection,
  effectiveSharedMessage,
} = usePriceRequestDetails(toRefs(props));

const { frameAgreement, isLoadingFrameAgreement, fetchFrameAgreement } =
  usePriceRequestFrameAgreement(
    props.tender,
    props.supplierId,
    hasFrameAgreement,
    customerOrganizationId,
  );

const {
  shouldShowFeedbackNotification,
  openFeedbackPanel,
  fetchAcceptanceData,
} = usePriceRequestFeedback(props);

const { getAttachment } = useFileAttachmentApi();
const { viewFileInNewWindow, viewOrderConfirmationDocument } =
  useFileDownload();
const router = useRouter();
const isWorkDescriptionTruncated = ref(true);
const isJoiningSurvey = ref(false);
const hasJoinedSurvey = ref(
  props.priceRequestDetails?.attendingSurvey === true,
);

const priceRequest = computed(() => props.tender.priceRequests?.[0]);

const mappedPriceRequestAttachments = computed(() =>
  (props.priceRequestDetails?.priceRequestAttachmentDtos || []).map(
    (attachment) => ({
      fileName: attachment.fileName,
      downloadHandler: async () => {
        try {
          const response = await getAttachment({
            attachmentId: attachment.attachmentId,
            tenderId: props.tender.tenderId,
            fileName: attachment.fileName,
            supplierId: props.supplierId,
          });

          if (!response?.data) {
            throw new Error("No response data");
          }

          await viewFileInNewWindow(response.data, router);
        } catch (error) {
          console.error("Error downloading attachment:", error);
        }
      },
    }),
  ),
);

const getFrameAgreementAttachments = (
  attachment: FrameAgreementAttachment,
): DisplayAttachment => ({
  fileName: attachment.fileName,
  downloadHandler: async () => {
    try {
      const response = await getAttachment({
        attachmentId: attachment.attachmentId,
        tenderId: props.tender.tenderId,
        fileName: attachment.fileName,
        supplierId: props.supplierId,
      });

      if (!response?.data) {
        throw new Error("No response data");
      }

      await viewFileInNewWindow(response.data, router);
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  },
});

const isAgreementRequestState = (state?: RequestStateType) => {
  const validStates = [
    RequestStateType.ACCEPTED,
    RequestStateType.REJECTED,
    RequestStateType.COMPLETED,
  ];
  return state ? validStates.includes(state) : false;
};

const getDocumentHandler = (
  document: (typeof agreementAndTermsCms)[0],
): (() => Promise<void>) => {
  const isAgreementDocument =
    document.title === AgreementAndTermsTitles.Agreement;

  if (isAgreementDocument) {
    return async () => {
      try {
        await viewOrderConfirmationDocument(
          props.tender?.tenderId,
          props.serviceOfferId,
          router,
        );
      } catch (error) {
        console.error("Error viewing order confirmation:", error);
      }
    };
  }

  return async () => {
    if (document.link) {
      window.open(document.link, "_blank");
    }
  };
};

const getDefaultAttachments = computed(() => {
  const requestState = props.priceRequestDetails
    ?.requestState as RequestStateType;

  // Show Agreement for valid states, Terms for others
  const documentToShow = agreementAndTermsCms.find(
    (doc) =>
      doc.title ===
      (isAgreementRequestState(requestState)
        ? AgreementAndTermsTitles.Agreement
        : AgreementAndTermsTitles.Terms),
  );

  return documentToShow
    ? [
        {
          fileName: documentToShow.title,
          downloadHandler: getDocumentHandler(documentToShow),
        },
      ]
    : [];
});

const displayedAttachments = computed(() => {
  const hasFrameAgreements =
    props.priceRequestDetails?.hasFrameAgreement &&
    frameAgreement.value?.attachments &&
    frameAgreement.value?.attachments?.length > 0;

  if (hasFrameAgreements) {
    return frameAgreement.value?.attachments.map(getFrameAgreementAttachments);
  }

  return getDefaultAttachments.value;
});

const isAttendSurveyVisible = computed(() => {
  const pr = props.priceRequestDetails;
  if (!pr) return false;
  // 1. Not accepted or rejected (pending)
  const state = pr.requestState as RequestStateType | undefined;
  if (
    state === RequestStateType.ACCEPTED ||
    state === RequestStateType.REJECTED
  )
    return false;
  // 2. Survey date is set
  if (!pr.surveyDateTime) return false;
  // 3. Survey date is today or in the future (timezone-safe)
  const now = new Date();
  const surveyDate = new Date(pr.surveyDateTime);
  // Only show if surveyDate is today or in the future
  return (
    surveyDate >= new Date(now.getFullYear(), now.getMonth(), now.getDate())
  );
});

const { setAttendingSurvey } = useTenderApi();

const toggleSurveyAttendance = async () => {
  console.log("toggle survey attendance", hasJoinedSurvey.value);
  if (!props?.supplierRequestId) return;
  isJoiningSurvey.value = true;
  try {
    await setAttendingSurvey({
      supplierId: props.supplierId,
      supplierRequestId: props.supplierRequestId,
      attending: hasJoinedSurvey.value,
    });
    emit("join-survey");
  } finally {
    isJoiningSurvey.value = false;
  }
};

const detailItems = computed(() => [
  {
    label: "Referansekode",
    value: effectiveReferenceCode.value,
    loading: false,
  },
  {
    label: "Tjenestekategori",
    value: effectiveServiceCategory.value,
    loading: false,
  },
  {
    label: "Forespørsel sendt",
    value: effectiveRequestSent.value,
    loading: false,
  },
  {
    label: "Svarfrist",
    value: props.deadlineDate,
    loading: false,
  },
  {
    label: "Ønsket oppstartsdato",
    value: effectiveDesiredStartDate.value,
    loading: false,
  },
  {
    label: "Kunde tags",
    value: "", // Empty value as we'll use custom template
    loading: false,
    isTags: true,
    tags: props.priceRequestDetails?.tags || [],
  },
  {
    label: "Felles befaring",
    value: effectiveJointInspection.value,
    loading: false,
    isSurvey: true, // Custom flag for template
  },
]);

const toggleWorkDescription = () => {
  isWorkDescriptionTruncated.value = !isWorkDescriptionTruncated.value;
};

const emit = defineEmits(["join-survey"]);

// Watch for changes in priceRequestDetails.attendingSurvey
watch(
  () => props.priceRequestDetails?.attendingSurvey,
  (newVal) => {
    hasJoinedSurvey.value = newVal === true;
  },
);

onMounted(async () => {
  await fetchFrameAgreement();
  await fetchAcceptanceData();
});
</script>

<style lang="scss" scoped>
.price-request-details {
  &__label {
    font-size: 16px;
    font-weight: 600;
    color: #1d1d1d;
    margin: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px 40px;
    margin-bottom: 40px;
  }

  &__full-width {
    margin-bottom: 40px;
  }

  &__divider {
    height: 1px;
    background: #dfcfe5;
    margin-bottom: 8px;
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__attachment {
    margin-bottom: 8px;
  }

  &__no-attachment {
    display: flex;
    justify-content: start;
    align-items: start;
    height: 100%;
  }

  &__no-attachment-text {
    font-size: 14px;
    font-weight: 400;
    color: #1d1d1d;
  }

  @media (max-width: 768px) {
    padding: 24px;

    &__grid {
      grid-template-columns: 1fr;
    }

    // Stack survey control vertically on mobile
    .details-item--has-survey {
      .details-item__label-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }

      .details-item__label-right {
        width: 100%;
        margin-left: 0;
      }
    }
  }

  // Override DetailsItem label-right positioning for survey control
  .details-item--has-survey {
    .details-item__label-right {
      margin-top: 0; // Remove the negative margin that misaligns titles
      align-self: flex-start; // Align to top of flex container
    }

    .details-item__label-row {
      align-items: flex-start; // Align items to top instead of center
      gap: 16px; // Add gap between title and control
    }
  }

  .survey-attendance-control {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    width: 180px; // Ensure consistent width
    transition: opacity 0.2s ease;

    &.is-disabled {
      opacity: 0.7;

      .survey-attendance-label {
        color: #6b7280;
      }

      .survey-attendance-description {
        color: #9ca3af;
      }
    }
  }

  .survey-attendance-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .survey-attendance-label {
    font-size: 14px;
    font-weight: 500;
    color: #1d1d1d;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  .survey-attendance-description {
    font-size: 9px;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.4;
    transition: color 0.2s ease;
  }

  .survey-toggle-switch {
    flex-shrink: 0;
    align-self: flex-start;
  }

  .customer-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .customer-tag {
    font-size: 12px;
    font-weight: 600;

    :deep(.p-tag .p-tag-secondary) {
      background: $color-accent;
      color: white;
      border: none;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      font-family: "Montserrat", sans-serif;
    }
  }

  .no-tags {
    font-size: 14px;
    font-weight: 400;
    color: $color-grey-dark;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

:deep(.p-tag-secondary) {
  background: $color-primary-pink-dark;
  color: $color-primary;
  font-size: 12px;
  font-weight: 600;
  font-family: "Montserrat", sans-serif;
}

.spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid #fff;
  border-top: 2px solid #1976d2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
